import { Component, OnInit, Input, Output, EventEmitter, OnChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';

import { AppMenuService, UtilitiesService } from '@nx-ahs/shared/services';
import { AppMenuItem } from '@nx-ahs/shared/models';
import { MenuIfDirective } from '@nx-ahs/shared/directives';

import { Observable, Subscription } from 'rxjs';

import { EnvironmentService } from '../../services/environment.service';
import { UserMenuPreferencesService } from '../../services/user-menu-preferences.service';
import { UserMenuHistoryService } from '../../services/user-menu-history.service';
import { MainMenuMode } from '../../../shared/interfaces/mainMenuModes';

import { SecurityService } from '../../services/security.service';
import version from '../../../../../../../version.json';
import { User } from '../../../modules/admin/models/user.model';
import { ServerInfoComponent } from '../../../modules/admin/server-info/server-info.component';
import { AboutHtComponent } from '../about-ht/about-ht.component';
import { SendKudosDialogComponent } from '../../../modules/game/send-kudos-dialog/send-kudos-dialog.component';
import { EncounterActionNoteManagementComponent } from '../../../modules/encounter/components/note-management/encounter-action-note-management.component';
import { CallActionsModalTemplateComponent } from '../../../modules/call-actions/call-actions-modal-template/call-actions-modal-template.component';
import { TaskService } from '../../services/task.service';
import { UserManagementService } from '../../services/user-management.service';
import { CustomComponentModel } from '@nx-ahs/script-collector';
import { FormlyCollectorModalComponent } from '@nx-ahs/formly/runner';
import { TimeoutService } from '@nx-ahs/timeout';
import { ApplicationMenuMenuItemsListComponent } from './application-menu-menu-items-list/application-menu-menu-items-list.component';
import { CALL_ACTIONS_DIALOG_SIZE } from '../../../modules/call-actions/call-actions-dialog.utility';

@Component({
	selector: 'app-application-menu',
	templateUrl: './application-menu.component.html',
	styleUrls: ['./application-menu.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		FontAwesomeModule,
		MatButtonModule,
		MatFormFieldModule,
		MatInputModule,
		MatMenuModule,
		MatTooltipModule,
		FlexLayoutModule,
		ApplicationMenuMenuItemsListComponent
	]
})
export class ApplicationMenuComponent implements OnInit, OnChanges, OnDestroy {
	@Input()
	public isMultiAppUser: boolean = false;

	@Input()
	public menuConfig?: AppMenuItem[] = [];

	@Input()
	public showHeader?: boolean = false;

	@Input()
	public user: User;

	@Input()
	public globalConditionEntities: Record<string, unknown>[];

	@Input()
	public menuToggled: Observable<void>;

	@Output()
	public menuItemClicked: EventEmitter<AppMenuItem> = new EventEmitter<AppMenuItem>();

	private subscriptions: Subscription[] = [];
	private readonly DEFAULT_MENU_MODE: MainMenuMode = { mainMenuMode: 'home' };

	public version: string = version.version;
	public filterText: string = '';
	public filteredMenuConfig: AppMenuItem[] = null;
	public currentMenuTypeConfig: AppMenuItem[] = null;
	public menuPadding = 16;
	public allowLabelToggle = true;

	constructor(
		private router: Router,
		private securityService: SecurityService,
		private dialogService: MatDialog,
		private snackBar: MatSnackBar,
		private appMenuService: AppMenuService,
		private environmentService: EnvironmentService,
		private utilities: UtilitiesService,
		private userMenuPreferencesService: UserMenuPreferencesService,
		private userMenuHistoryService: UserMenuHistoryService,
		private taskService: TaskService,
		private userManagementService: UserManagementService,
		private timeoutService: TimeoutService
	) {}

	ngOnChanges(changes) {
		if (changes?.menuConfig?.currentValue) {
			this.currentMenuTypeConfig = changes.menuConfig.currentValue;
		}
	}

	ngOnInit() {
		this.appMenuService.setMenuMode('home');
		this.configureloadMenuDefaultsSub();
		this.configureMenuItemFavoriteSubs();
		//Setting up for the clear menu
		this.subscriptions.push(
			this.appMenuService?.clearTextFlag?.subscribe((val) => {
				if (val) this.clearFilterMenuText();
			})
		);
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach((sub) => {
			sub.unsubscribe();
		});
	}

	public async addEditApplication(): Promise<void> {
		//let wizardModel = await this.applicationService.goToApplicationDashboard(37);
		//this.applicationService.addApplication()
	}

	public appMenuItemClicked(mi: AppMenuItem) {
		if (mi.methodName) {
			if (this[mi.methodName]) {
				this[mi.methodName](mi);
			}
		}

		this.appMenuService.setRecent(mi);
		this.menuItemClicked.emit(mi);
		this.userMenuHistoryService.logUserMenuHistory(mi.menuItemId);
	}

	public menuUpdated() {
		this.appMenuService.loadConfig(this.menuConfig);
	}

	public logout(): void {
		this.securityService.logout();
		this.menuItemClicked.emit(null);
	}

	public manageEncounterActionNotes(): void {
		this.dialogService.open(EncounterActionNoteManagementComponent, {
			disableClose: false,
			width: '40%',
			panelClass: 'default-modal',
			maxWidth: '600px'
		});
	}

	public openSearch(): void {
		this.router.navigate(['searches/search-list']);
		this.menuItemClicked.emit(null);
	}

	public openSendKudos(): void {
		this.dialogService.open(SendKudosDialogComponent, {
			disableClose: true,
			width: '40%',
			panelClass: 'default-modal',
			maxWidth: '600px'
		});
	}

	public refreshEnvironmentSettings(_mi: AppMenuItem): void {
		this.environmentService
			.refreshEnvironmentSettings()
			.then(() => {
				this.snackBar.open('Environment Settings refreshed successfully.', 'DISMISS', { duration: 5000 });
			})
			.catch(() => {
				this.snackBar.open('Environment Settings refresh failed.', 'DISMISS', { duration: 5000 });
			});
	}

	public refreshSecurity(_mi: AppMenuItem): void {
		this.securityService
			.refreshServerSecurity()
			.toPromise()
			.then((_result) => {
				this.snackBar.open('Server security refreshed successfully.', 'DISMISS', { duration: 5000 });
			})
			.catch((result) => {
				let err;

				// Attempt to retrieve the error from the returned data
				if (result.error.message) {
					err = result.error.message;
				} else {
					err = 'Unknown Error';
				}

				this.snackBar.open('Server security refresh failed.  ' + err, 'DISMISS', { duration: 5000 });
			});
	}

	public showAbout(): void {
		this.dialogService.open(AboutHtComponent, {
			disableClose: true,
			width: '40%',
			panelClass: 'default-modal',
			maxWidth: '700px'
		});
		this.menuItemClicked.emit(null);
	}

	public switchApp(): void {
		this.menuItemClicked.emit(null);
		this.securityService.clearApp();
		window.location.href = 'security/app-picker'; //Force a refresh so application insights can reset with the correct iKey
	}

	public userProfile(): void {
		this.router.navigate(['security/user-profile']);
		this.menuItemClicked.emit(null);
	}

	public viewServerInfo(): void {
		this.dialogService.open(ServerInfoComponent, {
			disableClose: true,
			width: '40%',
			panelClass: 'default-modal',
			maxWidth: '600px'
		});
	}

	public configureMenuItemFavoriteSubs(): void {
		this.subscriptions.push(
			this.userMenuPreferencesService.userFavoritesMenuUpdated.subscribe(async (_change) => {
				this.menuConfig = await this.userMenuPreferencesService.markMainMenuConfigFavorites(this.menuConfig);
			})
		);
	}

	public configureloadMenuDefaultsSub(): void {
		this.subscriptions.push(
			this.appMenuService.appMenuToggled.subscribe(() => {
				this.loadMenuDefaults();
			})
		);
	}

	public loadMenuDefaults() {
		if (this.getMainMenuMode() != this.DEFAULT_MENU_MODE.mainMenuMode) {
			this.loadMainMenuItems();
		}
	}

	public loadMainMenuItems() {
		this.appMenuService.setMenuMode('home');
		this.allowLabelToggle = true;
		this.clearFilterMenuText();
		this.currentMenuTypeConfig = this.menuConfig;
	}

	public loadFavoriteMenuItemsClick() {
		this.loadFavoriteMenuItems();
	}

	public loadMenuItemHistoryClick() {
		this.loadMenuItemHistory();
	}

	private async loadFavoriteMenuItems() {
		this.appMenuService.setMenuMode('favorites');
		this.allowLabelToggle = false;
		this.clearFilterMenuText();
		this.currentMenuTypeConfig = await this.userMenuPreferencesService.getUserFavoritedMenuItems();
	}

	private async loadMenuItemHistory() {
		this.appMenuService.setMenuMode('history');
		this.allowLabelToggle = false;
		this.clearFilterMenuText();
		this.currentMenuTypeConfig = await this.userMenuHistoryService.getUserMenuHistoryItems();
	}

	public filterMenu(filterText: string): void {
		this.filteredMenuConfig = JSON.parse(JSON.stringify(this.currentMenuTypeConfig)); // start with a fresh copy each time
		if (filterText.length >= 1) {
			let currentUserPermissions: number[];
			const currentApp = this.securityService.getCurrentApp();
			if (currentApp) {
				currentUserPermissions = currentApp.permissions;
			}

			this.filteredMenuConfig = this.filteredMenuConfig.filter((mi) =>
				MenuIfDirective.canDisplayItem(currentUserPermissions, mi, this.utilities, this.globalConditionEntities)
			); // filter out the items that won't be displayed, so the menu component does not display "no actions available"
			this.filteredMenuConfig = this.recursiveFilter(this.filteredMenuConfig, filterText);
		}
	}

	public getMainMenuMode(): string {
		return this.appMenuService.getMenuMode();
	}

	private recursiveFilter(menu: AppMenuItem[], filterText: string): AppMenuItem[] {
		menu.forEach((x) => {
			if (x.childMenu) {
				x.expanded = true;
				if (!x.title.toLowerCase().includes(filterText.toLowerCase())) {
					x.childMenu = this.recursiveFilter(x.childMenu, filterText);
				}
			}
		});

		return menu.filter((y) => {
			if (y.childMenu && y.childMenu.length > 0) {
				return true;
			}
			if (!y.childMenu) {
				if (y.title.toLowerCase().includes(filterText.toLowerCase())) {
					return true;
				}
			}
			return false;
		});
	}

	private clearFilterMenuText(): void {
		this.filterText = '';
		this.filteredMenuConfig = null;
	}

	private getTaskInbox(mi: AppMenuItem) {
		const taskMetadata = mi.metadata;
		let inboxId: string;
		if (taskMetadata.taskInboxId) {
			inboxId = Array.isArray(taskMetadata.taskInboxId) ? taskMetadata.taskInboxId[0] : taskMetadata.taskInboxId;
		}

		let queueId: string;
		if (taskMetadata.taskQueueId || taskMetadata.taskQueueIds) {
			queueId = taskMetadata.taskQueueId || taskMetadata.taskQueueIds[0];
		}

		let typeId: string;
		if (taskMetadata.taskTypeId || taskMetadata.taskTypeIds) {
			typeId = taskMetadata.taskTypeId || taskMetadata.taskTypeIds[0];
		}

		const commands = ['task-inbox'];
		if (inboxId) commands.push(inboxId);

		if (queueId) commands.push(queueId);

		if (typeId) commands.push(typeId);

		if (commands.length == 1) {
			commands.push(mi.title.replace(/\s+/g, ''));
		}

		this.taskService.menuItemClicked(mi);
		this.router.navigate(commands);
	}

	// For MA - Need to make generic using metadata
	public viewTrainerAssessmentForm(): void {
		this.dialogService.open(CallActionsModalTemplateComponent, {
			...CALL_ACTIONS_DIALOG_SIZE,
			data: {
				scriptId: 544,
				title: 'Trainer Assessment Form',
				confirmComplete: true
			}
		});
	}

	private openTask(taskId: number) {
		this.router.navigate([{ outlets: { sidebar: ['task-details-sidebar', taskId] } }]);
	}

	// a generic version of the method above - used for opening a case action like modal for certain menu items
	public openMenuItemModal(mi: AppMenuItem): void {
		// Update last action date
		this.timeoutService.actionTaken();
		if (mi.metadata?.formly) {
			this.dialogService
				.open(FormlyCollectorModalComponent, {
					data: {
						scriptId: mi.metadata.formly.scriptId,
						title: mi.metadata.title,
						confirmCancel: mi.metadata.confirmCancel,
						confirmComplete: mi.metadata.confirmComplete,
						overrides: mi.metadata.formly.overrides
					}
				})
				.afterClosed()
				.toPromise()
				.then((result) => {
					const displayTaskCreatedToast =
						this.securityService.getCurrentApp().application.clientConfig.tasks?.displayTaskCreatedSnackBar;
					if (
						result &&
						result.model &&
						(result.model.taskId || result.model.id) &&
						displayTaskCreatedToast &&
						mi.metadata.formly?.createsNewTask
					) {
						const taskId = result.model.taskId || result.model.id;
						const snackBarRef = this.snackBar.open('Task created successfully', `Task ${taskId}`, {
							duration: 7000
						});

						snackBarRef.onAction().subscribe(() => {
							this.openTask(taskId);
						});
					}
				});
		} else {
			this.dialogService.open(CallActionsModalTemplateComponent, {
				...CALL_ACTIONS_DIALOG_SIZE,
				data: {
					scriptId: mi.metadata?.scriptId,
					title: mi.metadata?.title,
					confirmCancel: mi.metadata?.confirmCancel,
					confirmComplete: mi.metadata?.confirmComplete,
					customComponents: (mi.metadata?.customComponents as CustomComponentModel[]) || []
				}
			});
		}
	}

	public setUserManagement(mi: AppMenuItem) {
		//Send the title which should be the name of the item the user clicked off the menu
		this.userManagementService.setStartPage(mi.title);
		this.router.navigate(['admin/user-management']);
	}
}
